/**
 * next-intl shim for Vite
 *
 * Provides a client-side implementation of the next-intl API surface:
 *   - useTranslations(namespace) → t() function
 *   - useLocale() → locale string
 *   - NextIntlClientProvider → pass-through (context is provided by IntlProvider)
 *   - IntlProvider → the real provider to wrap the Vite app
 *
 * Messages are loaded from the shared i18n/namespaces/ folder.
 * Locale is detected from the 'locale' cookie (same cookie next-intl sets).
 * Zero changes needed in any shared component.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { PageLoadingSkeleton } from '@/src/components/page-loading-skeleton'

// ─── Types ────────────────────────────────────────────────────────────────────

type Locale = 'en' | 'de'
type Messages = Record<string, unknown>

interface IntlContextValue {
  locale: Locale
  messages: Messages
  setLocale: (locale: Locale) => void
}

// ─── Context ──────────────────────────────────────────────────────────────────

const IntlContext = createContext<IntlContextValue>({
  locale: 'en',
  messages: {},
  setLocale: () => {},
})

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Resolve a dot-notation key path inside a nested object */
function resolvePath(obj: Record<string, unknown>, key: string): string {
  const parts = key.split('.')
  let current: unknown = obj
  for (const part of parts) {
    if (typeof current !== 'object' || current === null) return key
    current = (current as Record<string, unknown>)[part]
  }
  return typeof current === 'string' ? current : key
}

/** Replace {varName} placeholders with values */
function interpolate(
  str: string,
  values?: Record<string, string | number | Date>,
): string {
  if (!values) return str
  return str.replace(/\{(\w+)\}/g, (_, k) => String(values[k] ?? `{${k}}`))
}

// ─── Translation function type ────────────────────────────────────────────────

type RichTagRenderer = ((chunks: React.ReactNode) => React.ReactNode) | string | number

type TFunction = {
  (key: string, values?: Record<string, string | number | Date>): string
  rich: (
    key: string,
    tags: Record<string, RichTagRenderer>,
  ) => React.ReactNode
}

function createTranslator(namespace: Record<string, unknown>): TFunction {
  const t = (key: string, values?: Record<string, string | number | Date>): string => {
    const raw = resolvePath(namespace, key)
    return interpolate(raw, values)
  }

  t.rich = (
    key: string,
    tags: Record<string, RichTagRenderer>,
  ): React.ReactNode => {
    // Separate tag-renderer functions from plain interpolation values
    const tagFns = Object.fromEntries(
      Object.entries(tags).filter(([, v]) => typeof v === 'function'),
    ) as Record<string, (chunks: React.ReactNode) => React.ReactNode>
    const plainValues = Object.fromEntries(
      Object.entries(tags).filter(([, v]) => typeof v !== 'function'),
    ) as Record<string, string | number>

    // First substitute plain values (e.g. `email` in the string)
    let str = resolvePath(namespace, key)
    str = interpolate(str, plainValues)

    const tagNames = Object.keys(tagFns)
    if (tagNames.length === 0) return str

    const pattern = new RegExp(
      `<(${tagNames.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})>(.*?)<\\/\\1>`,
      'g',
    )

    const parts: React.ReactNode[] = []
    let lastIndex = 0
    let match: RegExpExecArray | null

    while ((match = pattern.exec(str)) !== null) {
      if (match.index > lastIndex) {
        parts.push(str.slice(lastIndex, match.index))
      }
      const tagName = match[1]
      const content = match[2]
      const renderer = tagFns[tagName]
      if (typeof renderer === 'function') {
        parts.push(
          React.createElement(React.Fragment, { key: match.index }, renderer(content)),
        )
      } else {
        parts.push(content)
      }
      lastIndex = match.index + match[0].length
    }

    if (lastIndex < str.length) {
      parts.push(str.slice(lastIndex))
    }

    return React.createElement(React.Fragment, null, ...parts)
  }

  return t as TFunction
}

// ─── Exported hooks ───────────────────────────────────────────────────────────

export function useTranslations(namespace: string): TFunction {
  const { messages } = useContext(IntlContext)
  // IMPORTANT: Must return a STABLE function reference across renders.
  // Shared components (e.g. OrganizationInitializer) put the `t` function in
  // useEffect deps. Without memoization, a new function is created every render
  // → the effect fires every render → infinite API call loop.
  //
  return useMemo(
    () => createTranslator((messages[namespace] ?? {}) as Record<string, unknown>),
    [messages, namespace],
  )
}

export function useLocale(): Locale {
  return useContext(IntlContext).locale
}

// ─── NextIntlClientProvider (pass-through) ────────────────────────────────────
// In Next.js, this wraps the app with messages from the server.
// In Vite, IntlProvider is the real provider. This component is a no-op
// kept for API compatibility (used in app/layout.tsx which isn't loaded in Vite).
export function NextIntlClientProvider({
  children,
  messages: _messages,
  locale: _locale,
}: {
  children: React.ReactNode
  messages?: unknown
  locale?: string
}) {
  return <>{children}</>
}

// ─── Locale persistence ───────────────────────────────────────────────────────

function detectLocale(): Locale {
  // 1. Check the 'locale' cookie (same cookie next-intl uses server-side)
  const match = document.cookie.match(/(?:^|;\s*)locale=([^;]+)/)
  if (match && (match[1] === 'en' || match[1] === 'de')) {
    return match[1] as Locale
  }
  // 2. Fall back to browser language
  const lang = navigator.language.split('-')[0]
  if (lang === 'de') return 'de'
  return 'en'
}

function saveLocale(locale: Locale): void {
  const maxAge = 60 * 60 * 24 * 365 // 1 year
  document.cookie = `locale=${locale};path=/;max-age=${maxAge};SameSite=Lax`
}

async function loadMessages(locale: Locale): Promise<Messages> {
  // Explicit branches let Vite statically analyze the dynamic imports
  // and split locale messages into separate chunks.
  if (locale === 'de') {
    return (await import('@/i18n/namespaces/index.de')).default as Messages
  }
  return (await import('@/i18n/namespaces/index.en')).default as Messages
}

// ─── IntlProvider (the real Vite-side provider) ───────────────────────────────

export function IntlProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>('en')
  const [messages, setMessages] = useState<Messages>({})
  const [ready, setReady] = useState(false)

  useEffect(() => {
    const detected = detectLocale()
    setLocaleState(detected)
    loadMessages(detected).then((msgs) => {
      setMessages(msgs)
      setReady(true)
    })
  }, [])

  const setLocale = useCallback((newLocale: Locale) => {
    saveLocale(newLocale)
    setLocaleState(newLocale)
    loadMessages(newLocale).then(setMessages)
  }, [])

  // Don't render children until translations are loaded to avoid flash
  if (!ready) {
    return <PageLoadingSkeleton />
  }

  return (
    <IntlContext.Provider value={{ locale, messages, setLocale }}>
      {children}
    </IntlContext.Provider>
  )
}

export { IntlContext }
